import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessageContext';
import { activeTabForPath } from './PrimaryNav';
import { HostIcon, hostAriaLabel, hostCountLabel, useHostCount } from './chrome/HostEntry';
import { HomeGlyph, TicketGlyph, FriendsGlyph, MessagesTabGlyph } from './chrome/headerIcons';
import { FAN_ITEMS, FanLayer, FanPlusGlyph, useFan } from './chrome/CreateFan';
import { useTabBarHidden, setTabBarHidden } from './chrome/tabBarVisibility';
import './chrome/topHeader.css';

/**
 * The global bottom nav, phone + tablet (< lg) — bottom-nav v2 handoff (Oct 2026), on the
 * top-header handoff §7 capsule.
 *
 *   A floating black capsule, 58px, max 520 wide and centred, 12px off the sides and 10px (plus
 *   the safe area) off the bottom. Five equal columns: HOME · EVENTS · HOST · FRIENDS · MESSAGES.
 *   Each tab is its icon in a 44×26 pill over a 9px label; the current one's pill takes the ON
 *   state (black inside the RING). FRIENDS plays its jump when tapped; MESSAGES, on the thumb
 *   side, wears the unread count. HOST is the raised 50px circle with the RING flowing round it
 *   — it no longer navigates: it fans three slabs up toward the thumb (the shared CreateFan).
 *
 * Mounted once at the app root. Hidden on surfaces that own their chrome: unauthenticated /
 * onboarding / auth routes, Portal Noir (its own gold shell), full-bleed ticket screens, and an
 * open chat thread (the inbox list keeps it).
 */

/** Bar + its bottom gap. The spacer and every "sits above the tab bar" offset use this. */
export const TAB_BAR_HEIGHT = 58;
export const TAB_BAR_GAP = 10;
/** The CSS length the page should leave free at the bottom while the bar is showing. */
export const TAB_BAR_CLEARANCE = `calc(${TAB_BAR_HEIGHT + TAB_BAR_GAP}px + env(safe-area-inset-bottom))`;

// Path prefixes where the bar must NOT appear.
const HIDE_PREFIXES = [
  '/login', '/register', '/forgot-password', '/reset-password', '/auth',
  '/onboard', '/party-preference',
  '/noir',   // Portal Noir renders its own scoped shell
  '/ticket',   // full-bleed ticket / success screens
  '/host/events/plan', // the planner is a takeover with its own EXIT and its own sticky
                       // note/approve bar; the capsule would sit on top of APPROVE
  '/terms',
  '/portal/edit', // the Portal Editor has its own sticky save bar on phones
];

/**
 * An open chat is a full-height screen with the composer at the bottom; the bar would sit on
 * it. The inbox list keeps the bar. Threads ride the URL as `/messages?thread=<id>`
 * (and `/messages/<id>` for older links).
 */
export function isOpenThread(pathname, search = '') {
  if (!pathname.startsWith('/messages')) return false;
  if (pathname.length > '/messages'.length && pathname.charAt('/messages'.length) === '/') return pathname.length > '/messages/'.length;
  return /[?&]thread=/.test(search);
}

const TABS = {
  home: { label: 'Home', path: '/square', Icon: HomeGlyph },
  events: { label: 'Events', path: '/featured', Icon: TicketGlyph },
  people: { label: 'Friends', path: '/people', Icon: FriendsGlyph },
  messages: { label: 'Messages', path: '/messages', Icon: MessagesTabGlyph },
};

const HOST_ITEMS = [FAN_ITEMS.portal, FAN_ITEMS.event, FAN_ITEMS.dashboard];
const HOST_SIZE = 50;

export default function MobileTabBar() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // MobileTabBar is mounted inside AuthProvider (App root), so useAuth is
  // always available here.
  const { isAuthenticated } = useAuth();
  const hidden =
    !isAuthenticated ||
    pathname === '/' ||
    isOpenThread(pathname, search) ||
    HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // Signed out there is nothing to count, and the request would only 401.
  const hostCount = useHostCount(undefined, { enabled: !hidden });
  // The provider is absent on unauthenticated routes; read it the way TopBar does.
  let unreadMessages = 0;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    unreadMessages = useMessages().unreadCount || 0;
  } catch { /* no MessageProvider */ }
  const fan = useFan();
  const circleRef = React.useRef(null);
  // A route change puts the fan away (a pick already did; this covers the back button) and
  // brings the bar back if a list had scrolled it off.
  const { dismiss } = fan;
  React.useEffect(() => { dismiss(); setTabBarHidden(false); }, [pathname, dismiss]);
  // Scrolled out of the way: the capsule slides down past the bottom edge (plus its gap and
  // the safe area) and slides back on an upward scroll. The fan keeps it on screen.
  const scrolledAway = useTabBarHidden() && !fan.mounted;

  if (hidden) return null;

  const current = activeTabForPath(pathname);

  const tab = (key) => {
    const { label, path, Icon } = TABS[key];
    const on = current === key;
    const badge = key === 'messages' && unreadMessages > 0 ? (unreadMessages > 9 ? '9+' : String(unreadMessages)) : null;
    const aria = key === 'messages' ? (unreadMessages > 0 ? `Messages · ${unreadMessages} new` : 'Messages') : undefined;
    return (
      <button
        key={key}
        type="button"
        data-portal-to={path}
        onClick={() => { if (!on) navigate(path); }}
        aria-current={on ? 'page' : undefined}
        aria-label={aria}
        className="th-tab"
      >
        <span className="th-tab-ico" style={{ position: 'relative' }}>
          {key === 'people' ? <Icon size={18} active={on} /> : <Icon size={18} />}
          {badge && (
            <span className="th-badge" data-testid="tab-messages-count" style={{ top: -8, right: -4, minWidth: 18, height: 18, padding: '0 4px' }}>{badge}</span>
          )}
        </span>
        <span className="th-tab-label">{label}</span>
      </button>
    );
  };

  const onHostRoute = current === 'host';
  const fanOpen = fan.mounted;
  const toggleHost = () => { if (fan.mounted) fan.close(); else fan.open(circleRef.current); };
  const pickHost = (item) => { fan.dismiss(); navigate(item.to); };

  // The circle: black face inside the animated RING at rest; white face with a black ×
  // while the fan is open (drawn again on top of the scrim by FanLayer).
  const circle = (inverted) => (
    <span
      className="th-host-ring relative flex items-center justify-center rounded-full"
      style={{
        width: HOST_SIZE, height: HOST_SIZE, border: '3px solid transparent', boxSizing: 'border-box', boxShadow: '0 0 0 4px #000',
        ...(inverted ? { background: 'linear-gradient(#fff,#fff) padding-box, var(--th-ring) border-box', backgroundSize: '100% 100%, 200% 100%', color: '#000' } : {}),
        transition: fan.reduced ? 'none' : 'background-color 180ms linear',
      }}
    >
      <span style={{ position: 'absolute', display: 'flex', opacity: inverted ? 0 : 1, transition: fan.reduced ? 'none' : 'opacity 120ms linear' }}>
        <HostIcon size={22} />
      </span>
      <span style={{ position: 'absolute', display: 'flex', opacity: inverted ? 1 : 0, transition: fan.reduced ? 'none' : 'opacity 120ms linear' }}>
        <FanPlusGlyph rotated={inverted} reduced={fan.reduced} size={22} strokeWidth={2.4} />
      </span>
      {!inverted && hostCount > 0 && (
        <span className="th-badge" data-testid="tab-host-count" style={{ top: -7, right: -7, minWidth: 20, height: 20, padding: '0 5px', fontSize: 10 }}>
          {hostCountLabel(hostCount)}
        </span>
      )}
    </span>
  );

  return (
    <>
      {/* Spacer reserves the bar, its gap and the safe-area inset so page content isn't hidden
          behind the fixed bar. */}
      <div className="lg:hidden" style={{ height: TAB_BAR_CLEARANCE }} aria-hidden />
      <nav
        data-pr-tabbar
        aria-label="Primary"
        data-scrolled-away={scrolledAway ? 'true' : undefined}
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 font-mono lg:hidden"
        style={{
          padding: `0 12px calc(${TAB_BAR_GAP}px + env(safe-area-inset-bottom))`,
          transform: scrolledAway ? `translateY(calc(100% + ${TAB_BAR_GAP}px))` : 'translateY(0)',
          transition: fan.reduced ? 'none' : 'transform 260ms cubic-bezier(.2,.7,.2,1)',
          willChange: 'transform',
        }}
        onKeyDown={fan.onKeyDownInFan}
      >
        <div
          className="pointer-events-auto relative mx-auto grid items-center rounded-full bg-black"
          style={{ gridTemplateColumns: 'repeat(5, minmax(0,1fr))', height: TAB_BAR_HEIGHT, maxWidth: 520, padding: '0 6px' }}
        >
          {tab('home')}
          {tab('events')}
          <button
            ref={fan.buttonRef}
            type="button"
            data-host-menu
            onClick={toggleHost}
            aria-haspopup="menu"
            aria-expanded={fanOpen}
            aria-label={fanOpen ? 'Close the host menu' : hostAriaLabel(hostCount)}
            aria-current={onHostRoute ? 'page' : undefined}
            className="th-tab rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            style={{ color: '#fff' }}
          >
            <span ref={circleRef} style={{ marginTop: -20, display: 'flex' }}>{circle(false)}</span>
            <span className="th-tab-label" style={{ color: fanOpen || onHostRoute ? '#fff' : '#8A8A8A' }}>Host</span>
          </button>
          {tab('people')}
          {tab('messages')}
        </div>
      </nav>

      <FanLayer
        fan={fan}
        items={HOST_ITEMS}
        origin="tabbar"
        anchorSize={HOST_SIZE}
        onPick={pickHost}
        menuLabel="Host"
        face={circle(true)}
      />
    </>
  );
}
